import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useState } from "react";
import { useBlocker, useNavigate } from "react-router-dom";

export const useConfirmNavigate = (
  blocking: boolean | (() => boolean),
  {
    message = "You have unsaved changes on this page. Are you sure you want to leave?",
  }: { message?: string } = {}
) => {
  // normalize 'blocking' to a function
  const latestBlocking = useFutureCallback(
    typeof blocking === "boolean"
      ? blocking
        ? () => true
        : () => false
      : blocking
  );

  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);

  useBlocker(({ nextLocation }) => {
    // allow confirmed navigation
    if (navigateTo != null && nextLocation.pathname === navigateTo) {
      return false;
    }

    // check if confirmation should show
    const block = latestBlocking();

    if (block) {
      // navigation must be confirmed: set path and open confirmation dialog
      setNavigateTo(nextLocation.pathname);
      setOpen(true);
      return true;
    }

    // navigation not blocked: no confirmation needed
    return false;
  });

  return (
    <ConfirmDialog
      open={open}
      setOpen={setOpen}
      message={message}
      cancelLabel="Don't Leave"
      confirmLabel="Leave"
      cancelVariant="default"
      confirmVariant="destructive"
      onCancel={() => {
        setNavigateTo(null);
      }}
      onConfirm={() => {
        if (navigateTo != null) navigate(navigateTo);
      }}
    />
  );
};
